import React, { useEffect, useRef, useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Input } from '../../components/ui/input';

// v4.37.1 item 2: search an employee by NAME or EMPLOYEE NUMBER and show BOTH
// (plus work email) before anything is saved — replaces raw "Employee id" boxes
// in deductions, elections and garnishments.
const empLabel = (e) => {
  const name = [e.last_name, e.first_name].filter(Boolean).join(', ') || e.preferred_name || '(no name)';
  return `${name}${e.employee_number ? ` · #${e.employee_number}` : ''}`;
};

export const EmployeePicker = ({ value, onChange, testid = 'emp' }) => {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const boxRef = useRef(null);

  const ensureLoaded = async () => {
    if (all) return;
    try { const r = await payrollAPI.employeeDirectory(); setAll(r.data || []); }
    catch { setAll([]); }
  };

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Clearing the value from the parent (e.g. after a successful save) resets the chip.
  useEffect(() => { if (!value) setSelected(null); }, [value]);

  const matches = (all || []).filter((e) => {
    const t = q.trim().toLowerCase();
    if (!t) return false;
    const name = `${e.first_name || ''} ${e.last_name || ''} ${e.preferred_name || ''}`.toLowerCase();
    return name.includes(t) || String(e.employee_number || '').toLowerCase().includes(t);
  }).slice(0, 25);

  const choose = (e) => {
    setSelected(e);
    setQ('');
    setOpen(false);
    onChange && onChange(e);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        data-testid={`${testid}-search`}
        placeholder="Search by name or employee #"
        value={q}
        onFocus={() => { ensureLoaded(); setOpen(true); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
      />
      {open && q.trim() && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto border rounded bg-popover text-popover-foreground shadow-lg text-sm" data-testid={`${testid}-results`}>
          {matches.length === 0 && <div className="p-2 text-muted-foreground">{all ? 'No matching employee' : 'Loading directory…'}</div>}
          {matches.map((e) => (
            <button
              type="button"
              key={e.id}
              data-testid={`${testid}-opt-${e.id}`}
              onClick={() => choose(e)}
              className="block w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground"
            >
              <div className="font-medium">{empLabel(e)}</div>
              {e.email && <div className="text-xs text-muted-foreground">{e.email}</div>}
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div className="mt-1 text-xs text-emerald-700" data-testid={`${testid}-selected`}>
          Selected: <b>{empLabel(selected)}</b>{selected.email ? ` — ${selected.email}` : ''}
        </div>
      )}
    </div>
  );
};
